import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { quickCommerceApiColletions } from "src/app/core/constants/api_collection";
import { ApiService } from "src/app/shared/services/api.service";
import { Cart } from "../models/cart.model";
import { Variant } from "../models/category-group.model";

@Injectable({
  providedIn: "root"
})
export class QCommerceService {
  // Use a BehaviorSubject to store the cart items
  private cartItems = new BehaviorSubject<Cart[]>([]);

  // Observable to expose the cart items
  cartItems$ = this.cartItems.asObservable();

  // Subject to hold the selected category ID
  private selectedCategoryIdSubject = new Subject<string>();

  // Observable to expose the selected category ID
  selectedCategoryId$ = this.selectedCategoryIdSubject.asObservable();

  // BehaviourSubject to hold the city setting
  private citySettingSubject = new BehaviorSubject<any>(null);

  // Observable to expose the city setting
  citySettingSubject$ = this.citySettingSubject.asObservable();

  constructor(private apiService: ApiService) {}

  /**
   * Fetches the list of main categories using the ApiService.
   * Subscribes to the service's observable to receive the category data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the category data.
   */
  getMainCategoryList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.apiService.get({ url: quickCommerceApiColletions.mainCategory, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of groups using the ApiService.
   * Subscribes to the service's observable to receive the group data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the group data.
   */
  getGroupList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.group}`;
      this.apiService.get({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of tags using the ApiService.
   * Subscribes to the service's observable to receive the tag data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the tag data.
   */
  getTags(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.tag}`;
      this.apiService.get({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of attribute groups using the ApiService.
   * Subscribes to the service's observable to receive the attribute group data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @returns An observable of the attribute group data.
   */
  getAttributeGroupList(): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.attributeGroup}`;
      this.apiService.get({ url }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of products using the ApiService.
   * Subscribes to the service's observable to receive the product data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the product data.
   */
  getProductList(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.product}/list`;
      this.apiService.get({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the list of categories for the specified category ID using the ApiService.
   * Subscribes to the observable returned by the service and updates the observer with the response data.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The query parameters to be passed to the API.
   * @param categoryId The ID of the category for which to fetch the categories.
   * @returns An observable of the category data.
   */
  getCategoryList(parameters: any, categoryId: string): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.category}/list/${categoryId}`;
      this.apiService.get({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Updates the selected category ID observable with the given category ID.
   * This will cause all subscribers to the selectedCategoryId$ observable to receive the new category ID.
   * @param categoryId The ID of the category to select.
   */
  updateSelectedCategoryId(categoryId: string): void {
    this.selectedCategoryIdSubject.next(categoryId);
  }

  /**
   * Updates the current state of the cart with the given array of `Cart` items.
   * This will cause all subscribers to the `cartItems$` observable to receive the new cart items.
   * @param cartItems The array of `Cart` items to update the cart with.
   */
  updateCartItems(cartItems: Cart[]): void {
    this.cartItems.next(cartItems);
  }

  /**
   * Adds a product variant to the cart. If the product variant is already present in the cart,
   * the quantity will be incremented by the given quantity, up to the maximum allowed in the cart.
   * Otherwise, the product variant will be added with an initial quantity of 1.
   *
   * @param item - The cart item to add to the cart.
   * @param variant - The product variant to add to the cart.
   */
  addToCart(item: Cart, variant: Variant): void {
    const currentCart = this.cartItems.value.length ? [...this.cartItems.value] : [...JSON.parse(localStorage.getItem("cartItems") || "[]")];
    const existingItem = currentCart.find((cartItem) => cartItem.productId === item.productId && cartItem.variantId === item.variantId);
    if (existingItem) {
      existingItem.qty = Math.min(existingItem.qty + item.qty, variant.maxQtyAddInCart);
    } else {
      item.qty = Math.min(item.qty, variant.maxQtyAddInCart);
      currentCart.push(item);
    }
    localStorage.setItem("cartItems", JSON.stringify(currentCart));
    this.cartItems.next(currentCart);
  }

  /**
   * Removes a product variant from the cart.
   * If the specified product variant is in the cart, it will be removed; otherwise, the cart remains unchanged.
   * @param productId - The ID of the product to remove from the cart.
   * @param variantId - The ID of the variant to remove from the cart.
   */
  removeFromCart(productId: string, variant: any): void {
    const currentCart = JSON.parse(localStorage.getItem("cartItems") || "[]").filter(
      (cartItem: Cart) => !(cartItem.productId === productId && cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant))
    );
    localStorage.setItem("cartItems", JSON.stringify(currentCart));
    this.cartItems.next(currentCart);
  }

  /**
   * Updates the quantity of a product variant in the cart.
   * If the product variant is in the cart, the quantity will be updated to the given quantity,
   * up to the maximum allowed in the cart (maxQtyAddInCart).
   * If the product variant is not in the cart, the cart remains unchanged.
   * @param productId The ID of the product to update in the cart.
   * @param variantId The ID of the variant to update in the cart.
   * @param quantity The new quantity of the product variant in the cart.
   * @param maxQtyAddInCart The maximum quantity that can be added to the cart for this product variant.
   */
  updateQuantity(productId: string, variant: any, quantity: number, maxQtyAddInCart: number): void {
    const currentCart: Cart[] = JSON.parse(localStorage.getItem("cartItems") || "[]");
    const itemIndex = currentCart.findIndex((cartItem) => cartItem.productId === productId && cartItem.variantId === (typeof variant === "object" ? variant.variantId : variant));
    if (itemIndex !== -1) {
      // Update the quantity of the item (ensure it doesn't exceed maxQtyAddInCart)
      currentCart[itemIndex].qty = Math.min(quantity, maxQtyAddInCart);
      localStorage.setItem("cartItems", JSON.stringify(currentCart));
      // Emit the updated cart
      this.cartItems.next(currentCart);
    }
  }

  /**
   * Checks if a business is available for ordering using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  checkQuickCommerceBusiness(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.checkBusiness}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the cart data using the ApiService.
   * Subscribes to the service's observable to receive the cart data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the cart data.
   */
  getCart(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.cart}`;
      this.apiService.post({ url, parameters }).subscribe({
        next: (response) => {
          if (response.data) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Places an order using the ApiService.
   * Subscribes to the service's observable to receive the response data, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param parameters The parameters to be passed as query parameters to the API.
   * @returns An observable of the response data.
   */
  placeOrder(parameters: any): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.booking}`;
      this.apiService.post({ url: url, parameters }).subscribe({
        next: (response) => {
          if (response) {
            observer.next(response);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Fetches the brand details using the ApiService.
   * Subscribes to the service's observable to receive the brand details, updates the observer with the response data and completes the observable.
   * If there is an error, it updates the observer with false and completes the observable.
   * @param brandId The ID of the brand to fetch details for.
   * @returns An observable of the response data.
   */
  getBrandDetail(brandId: string): Observable<any> {
    return new Observable<any>((observer) => {
      const url = `${quickCommerceApiColletions.brand}/${brandId}`;
      this.apiService.get({ url: url }).subscribe({
        next: (response) => {
          if (response) {
            observer.next(response.data.data);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: () => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Updates the current city setting with the provided value.
   * This will notify all subscribers of the citySettingSubject$ observable.
   * @param citySetting The new city setting to be applied.
   */
  updateCitySetting(citySetting: any): void {
    this.citySettingSubject.next(citySetting);
  }
}
